// ============================================================================
// MAGASIN & CAISSES — la topologie du site, portée du DOM de la maquette, mesures comprises.
//
// DEUX PARTIES — la boutique, puis ce qui encaisse — et TROIS SOURCES : la fiche du magasin (réglages `company.*` + GLN), les caisses
// DÉCLARÉES (`device_registry`, éclairées par le battement de synchronisation), et les groupes
// d'affichage (`register_group`, propres au back-office).
//
// ⚠️ LES GROUPES NE DESCENDENT JAMAIS AUX CAISSES, et la maquette le dit à l'écran — ce n'est pas une
// note technique mais une PROMESSE faite à l'exploitant : ranger ses caisses en « 2ᵉ étage » ne
// change rien à ce qu'elles font. Le texte reste, mot pour mot.
//
// ⚠️ « ON NE SAIT PAS » ET « IL N'Y EN A PAS » SE DISENT DIFFÉREMMENT. Un magasin jamais synchronisé
// n'a pas de registre d'appareils : le serveur rend `null`, et l'écran l'annonce. Rendre un tableau
// vide affirmerait « aucune caisse » — ce qui envoie chercher une panne de matériel là où il n'y a
// qu'une base neuve (§23.0).
//
// ⚠️ RELEVÉ DU HTML BRUT, PAS DE LA STRUCTURE SEULE (leçon de l'écran Paramètres, Forme AS) : grille
// `auto-fit minmax(min(100%, 330px), 1fr)` par partie, cartes `16px 18px`, lignes de caisse `10px 16px`, pastilles de
// groupe en bordure 1,5 px, pied de création sur fond `ink-50`.
// ============================================================================
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirm, EmptyState, Icon, showToast, Tooltip, Trans } from '@orgazone/ui-web';
import { api, type RegisterGroup, type StoreCalendar, type StoreHours, type StoreRegisterRow, type StoreTerminalRow, type StoreTopology } from '../api';
// ⚠️ LE MÊME RENDU QUE PARTOUT AILLEURS (journal, clôtures, articles rapides) : la base scelle le
//    code COURT (« 1 »), les écrans montrent « R01 ». Recopier la règle ici aurait fini par afficher
//    « R01 » d'un côté et « 1 » de l'autre pour la même caisse — un lecteur y verrait deux caisses.
import { codeCaisse } from '../caisse';
import { formatPhoneForDisplay } from '@orgazone/core-crm';
import { CarteHoraires } from './magasin-horaires';
import { DELIVERY_VIEW } from './reglages-recus';
import { t } from '../i18n';

/** Au-delà de ce délai sans battement, une caisse n'est plus « en service » (même seuil qu'au parc). */
const EN_LIGNE_APRES_MIN = 15;

export function Magasin() {
  const [vue, setVue] = useState<StoreTopology | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [nouveau, setNouveau] = useState('');
  const [horaires, setHoraires] = useState<StoreHours | null>(null);
  /** Le calendrier des envois — `null` tant qu’il n’est pas chargé, jamais un objet inventé. */
  const [calendrier, setCalendrier] = useState<StoreCalendar | null>(null);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(() => {
    api.storeTopology().then((v) => { setVue(v); setErreur(null); })
      .catch((e) => setErreur((e as Error).message));
    // ⚠️ Lecture SÉPARÉE, et volontairement : des horaires illisibles ne doivent pas emporter la
    //    fiche du magasin ni ses caisses. Chaque bloc de cet écran tombe seul.
    api.storeHours().then(setHoraires).catch(() => setHoraires(null));
    // ⚠️ CHARGÉ À PART DES HORAIRES : ce sont deux faits distincts, et un échec de lecture sur l’un
    //    ne doit pas priver l’écran de l’autre.
    void api.storeCalendar().then(setCalendrier).catch(() => setCalendrier(null));
  }, []);
  useEffect(() => { charger(); }, [charger]);

  /** Un geste d'écriture : le serveur rend la topologie à jour, l'écran ne recompose jamais la sienne. */
  const agir = async (quoi: () => Promise<StoreTopology>, succes?: string) => {
    if (busy) return;
    setBusy(true);
    try {
      setVue(await quoi());
      if (succes) showToast(succes);
    } catch (e) {
      showToast((e as Error).message, 'err', 9000);
    } finally { setBusy(false); }
  };

  return (
    <div>
      {/* En-tête — même gabarit que les autres écrans de Réglages. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Magasin & caisses')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            <Trans k='La topologie du site : la fiche du magasin, ses caisses, et vos <1>groupes d’affichage</1>.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
      </div>

      {erreur != null ? (
        <EmptyState icon="alert" title={t('Topologie indisponible')} note={erreur} />
      ) : vue === null ? (
        <div style={{ padding: 34, textAlign: 'center', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Chargement…')}</div>
      ) : (
        // ⚠️ DEUX PARTIES (dessin, à la demande du gérant) : la BOUTIQUE (son identité, ses horaires) et
        //    ce qui ENCAISSE ou compte (caisses, groupes, Mobile Partner). Chaque partie est sa propre grille : ses
        //    cartes s'alignent entre elles, jamais avec celles de l'autre partie.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <Partie title={t('La boutique')} note={t('Identité, horaires et journée fiscale')}>
            <CarteMagasin vue={vue} />
            {/* ⚠️ 🔴 UNE CARTE, DEUX ENREGISTREMENTS : on répond à « quand ce magasin est-il ouvert ? » à UN endroit
                (le dessin), mais ce ne sont PAS les mêmes faits — les heures commandent la journée fiscale, les jours
                et le fuseau l'heure des envois — donc chaque partie garde son geste. */}
            <CarteHoraires
              horaires={horaires}
              onEnregistrer={async (open, close) => {
                try {
                  setHoraires(await api.saveStoreHours(open, close));
                  showToast(t('Horaires enregistrés.'));
                } catch (e) { showToast((e as Error).message, 'err', 9000); }
              }}
              calendar={calendrier}
              onSaveCalendar={async (timezone, openDays) => {
                const r = await api.saveStoreCalendar(timezone, openDays);
                setCalendrier(r);
                return r;
              }}
            />
          </Partie>
          <Partie title={t('Caisses & appareils')} note={t('Les caisses, les Mobile Partner et les groupes d’affichage')}>
            <CarteCaisses registers={vue.registers} />
            <CarteGroupes
              vue={vue} nouveau={nouveau} setNouveau={setNouveau} busy={busy}
              onCreer={() => {
                const nom = nouveau.trim();
                if (!nom) return;
                void agir(() => api.createRegisterGroup(nom), t('Groupe « {nom} » créé.', { nom })).then(() => setNouveau(''));
              }}
              onBasculer={(g, registerId) =>
                void agir(() => api.setRegisterGroupMember(g.id, registerId, !g.registerIds.includes(registerId)))}
              onSupprimer={async (g) => {
                const oui = await confirm({
                  title: t('Supprimer « {name} » ?', { name: g.name }),
                  message: t('Ce groupe n’est qu’un raccourci d’affichage : sa suppression ne change rien aux caisses ni à leurs réglages.'),
                  confirmLabel: t('Supprimer'),
                  tone: 'danger',
                });
                if (oui) void agir(() => api.deleteRegisterGroup(g.id), t('Groupe « {nom} » supprimé.', { nom: g.name }));
              }}
            />
            <CarteTerminaux terminals={vue.terminals} />
          </Partie>
        </div>
      )}
    </div>
  );
}

/**
 * UNE PARTIE DE L'ÉCRAN — son titre, sa phrase, et la grille de ses cartes.
 * ⚠️ `stretch` : les cartes d'une même rangée prennent la même hauteur (dessin) ; `min(100%, 330px)` laisse une carte
 *    tenir sur un écran plus étroit que 330 px au lieu de déborder.
 */
function Partie({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px 10px', flexWrap: 'wrap', margin: '0 0 10px' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--ink-900)' }}>{title}</h2>
        <span style={{ fontSize: '12.5px', color: 'var(--ink-500)' }}>{note}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,330px),1fr))', gap: 10, alignItems: 'stretch' }}>
        {children}
      </div>
    </section>
  );
}

/** Une coordonnée du point de vente sous l'identité — « — » quand elle n'est pas renseignée, comme le dessin. */
function LigneCoordonnee({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-100)' }}>
      <span style={{ flex: 1, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{label}</span>
      <span style={{ fontFamily: mono ? 'var(--font-mono)' : undefined, fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-800)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value ?? '—'}
      </span>
    </div>
  );
}

/** La fiche du magasin — ce que la création de la base a posé, et qui ne se corrige pas ici. */
function CarteMagasin({ vue }: { vue: StoreTopology }) {
  const { gln, legalName, address, phone, email } = vue.store;
  const navigate = useNavigate();
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', padding: '16px 18px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ width: 34, height: 34, borderRadius: 'var(--r-ctrl)', background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {/* ⚠️ 21 ET NON 17 (retour utilisateur 2026-09-07 : « trop petite dans son cadre »). Le
              CADRE ne bouge pas — c'est un repère visuel réutilisé ailleurs ; seul le dessin
              grandit, pour occuper ~62 % de la pastille au lieu de la moitié. */}
          <Icon name="building" size={21} stroke={1.8} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>
            {legalName ?? <span style={{ color: 'var(--ink-400)', fontWeight: 500 }}>{t('Raison sociale non renseignée')}</span>}
          </span>
          <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
            {address ?? t('Adresse non renseignée')}
          </span>
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-100)' }}>
          {/* ⚠️ « GLN — pivot du magasin » (maquette) remplacé par « Identifiant unique » (décision
              utilisateur 2026-09-07) : « pivot » décrit notre architecture, pas ce que le numéro EST
              pour celui qui le lit. Un libellé doit parler à qui le lit, pas à qui l'a codé. */}
          <span style={{ flex: 1, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Identifiant unique')}</span>
          {/* Le cadenas dit l'IMMUABILITÉ, pas un droit : ce numéro ne se change nulle part. */}
          <Tooltip label={t('Identifiant immuable')} content={t('Le GLN identifie la base de ce magasin et les chaînes de signature de ses caisses. Il est posé à la création et ne se change jamais.')}>
            <Icon name="lock" size={11} stroke={2.2} color="var(--ink-500)" />
          </Tooltip>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-800)' }}>{gln}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--ink-50)' }}>
          <span style={{ flex: 1, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Caisses déclarées')}</span>
          <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-800)' }}>
            {/* ⚠️ La maquette annonce ici « SLIDERZ SAS · 4 sites ». Le nombre de SITES d'une enseigne
                n'est pas connu de la base d'un magasin — seule la Console le sait. On met à sa place
                ce qui EST mesuré : le nombre de caisses de CE magasin (§23.0). */}
            {vue.registers === null ? t('non mesuré') : `${vue.registers.length}`}
          </span>
        </div>
        <LigneCoordonnee label={t('Téléphone')} value={phone === null ? null : formatPhoneForDisplay(phone)} mono />
        <LigneCoordonnee label={t('E-mail de contact')} value={email} />
      </div>
      {/* ⚠️ LE LOGO DU DESSIN N'A PAS ENCORE SA LIGNE : il ne se dépose nulle part (tranche « Le logo » du plan de la
          maquette du 2026-10-31). Une invitation « à déposer dans Paramètres » enverrait chercher un champ absent. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px 12px', marginTop: 'auto', paddingTop: 14, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
          <Trans k='Le GLN est <1>immuable</1>. L’adresse, le téléphone et l’e-mail se règlent dans Paramètres → Société & identité.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
        </span>
        <button type="button" onClick={() => navigate('/parametres?onglet=company')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 34, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)', color: '#fff', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0 }}>
          <Icon name="edit" size={14} stroke={1.9} />
          {t('Modifier les coordonnées')}
        </button>
      </div>
    </div>
  );
}

/** Les caisses déclarées au registre — le battement de sync ne fait que les éclairer. */
/**
 * CE QUE CES LISTES SONT, ET CE QU'ELLES NE SONT PAS.
 *
 * ⚠️ 🔴 LE BACK-OFFICE MONTRE CE QUE LA BASE DU MAGASIN CONNAÎT, PAS CE QUE LA CONSOLE DÉCLARE. Ce
 * sont deux systèmes : la Console affecte un appareil dans SON annuaire, puis le POUSSE au magasin.
 * Quand la poussée n’a pas abouti, l’écran affiche une liste juste mais INCOMPLÈTE — et rien ne
 * distingue « ce magasin a deux caisses » de « il en a quatre, dont deux jamais déclarées ».
 *
 * ⚠️ POURQUOI UNE PHRASE ET NON UN COMPTE « 2 SUR 4 » : le back-office ne sait PAS combien la Console
 * en déclare. L’inventer serait exactement le mensonge que §23.0 interdit. Il peut seulement dire d’où
 * vient sa liste, et nommer le geste qui la complète.
 *
 * ⚠️ TOUJOURS VISIBLE, pas seulement sur une liste vide : le cas qui a fait naître cette phrase était
 * une liste NON VIDE (deux caisses sur quatre affectées). Un état vide se remarque ; un manque partiel
 * ne se remarque jamais.
 */
export function phraseProvenance(quoi: string): string {
  return t('Un appareil affecté dans la Console apparaît ici dès qu’elle l’a déclaré au magasin.')
    + ' ' + t('Si {quoi} y manque, re-déclarez le magasin depuis la Console : le geste repousse la liste complète.', { quoi });
}

function CarteCaisses({ registers }: { registers: StoreRegisterRow[] | null }) {
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 16px', borderBottom: '1px solid var(--ink-100)' }}>
        <span style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', flex: 1 }}>{t('Caisses déclarées')}</span>
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
          {registers === null ? t('non mesuré')
            : t('{n} caisse', { n: registers.length })}
        </span>
      </div>

      {registers === null ? (
        // ⚠️ Ni tableau vide, ni erreur bloquante : ce magasin n'a simplement jamais synchronisé.
        <EmptyState icon="tablet" title={t('Registre non lisible')}
          note={t('Aucune caisse n’a encore synchronisé avec ce magasin — le registre d’appareils n’existe donc pas. Il se crée tout seul à la première synchronisation.')} />
      ) : registers.length === 0 ? (
        <EmptyState icon="tablet" title={t('Aucune caisse déclarée')}
          note={t('Une caisse apparaît ici dès que la Console l’a déclarée — même avant son premier branchement. Elle s’ajoute depuis la Console Orgazone.')} />
      ) : registers.map((c) => <LigneCaisse key={c.registerId} c={c} />)}

      <div style={{ padding: '12px 16px', background: 'var(--ink-50)', fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.6 }}>

        {/* 🔴 CETTE PHRASE PROMETTAIT CE QUE LE CODE NE FAIT PAS. Elle disait que les périphériques
            « se corrigent sur place et REMONTENT ICI » : rien ne remonte. Le canal `peripheral` est
            descendant, la table est écrite par personne et lue par personne, et les réglages de TPE
            et d'imprimante sont `device_local` — ils ne quittent jamais la tablette. Un commerçant
            qui lisait ça croyait que le siège voyait son parc (§23.0). L'échange bidirectionnel est
            DÉCIDÉ (CADRAGE-Peripheriques-Bidirectionnel) ; tant qu'il n'est pas livré, l'écran dit
            ce qui est, pas ce qui viendra. */}
        <Trans
          k='Les réglages <1>descendent</1> du back-office vers les caisses, jamais l’inverse. Les <2>périphériques</2> (TPE, imprimante) se règlent aujourd’hui sur la caisse et n’en sortent pas. Une caisse s’ajoute depuis la Console Orgazone.'
          parts={[(x) => <strong style={{ color: 'var(--ink-800)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-800)' }}>{x}</strong>]} />
        {/* ⚠️ LA PHRASE QUI MANQUAIT : « une caisse s’ajoute depuis la Console » dit comment en AJOUTER
            une, jamais quoi faire quand une caisse déjà affectée n’apparaît PAS. C’est pourtant ce
            deuxième cas qu’on rencontre, et il se lit alors comme un défaut d’affichage. */}
        {' '}{phraseProvenance(t('une caisse déjà affectée'))}
      </div>
    </div>
  );
}

/** Une caisse : son n°, son état de synchronisation, et si elle a reçu le dernier réglage enregistré. */
function LigneCaisse({ c }: { c: StoreRegisterRow }) {
  const vue = etatCaisse(c.lastSyncAt);
  const recu = c.settings === null ? null : DELIVERY_VIEW[c.settings];
  // ⚠️ « REÇOIT LA DERNIÈRE MODIFICATION » SEULEMENT SI ELLE EST EN SERVICE : une caisse éteinte ne reçoit rien
  //    tant qu'on ne l'a pas rallumée, et sa ligne le dit déjà (« vue il y a 3 j »).
  const sous = c.settings === 'never' ? t('Appairage à faire depuis la Console')
    : c.settings === 'pending' && vue.enService ? `${vue.sous} — ${t('reçoit la dernière modification')}`
      : vue.sous;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 16px', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
      <span style={{ width: 30, height: 30, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {/* Même correction que la pastille de la fiche : le cadre de 30 reste, le dessin passe à 18. */}
        <Icon name="tablet" size={18} stroke={1.8} />
      </span>
      <span style={{ flex: 1, minWidth: 120 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{codeCaisse(c.registerId)}</span>
          <span aria-hidden style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: vue.point }} />
        </span>
        <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 1 }}>{sous}</span>
      </span>
      {/* ⚠️ UN JOURNAL ILLISIBLE NE JUGE PAS : « non mesuré », jamais un « à jour » de repli. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: recu?.bg ?? 'var(--ink-100)', color: recu?.fg ?? 'var(--ink-600)', fontSize: 'var(--t-sub)', fontWeight: 700, whiteSpace: 'nowrap' }}>
        {recu ? t(recu.label) : t('non mesuré')}
      </span>
    </div>
  );
}

/**
 * Les TERMINAUX DE STOCK (Sliderz Mobile Partner) — des machines du parc, listées À PART des caisses parce
 * qu'ils n'en sont pas : ni n° de caisse, ni chaîne NF525, mais un battement de synchronisation.
 * ⚠️ LE CODE VIENT DE LA CONSOLE (« MP01 ») ; sans fiche, l'identifiant d'appareil est montré tel quel,
 *    tronqué — une machine qui parle au siège se voit, même quand personne ne l'a encore nommée.
 */
function CarteTerminaux({ terminals }: { terminals: StoreTerminalRow[] | null }) {
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 16px', borderBottom: '1px solid var(--ink-100)' }}>
        <span style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)', flex: 1 }}>{t('Terminaux de stock')}</span>
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
          {terminals === null ? t('non mesuré') : t('{n} terminal', { n: terminals.length })}
        </span>
      </div>

      {/* ⚠️ L’ÉTAT VIDE DISAIT « dès sa première synchronisation », ce qui est une DEMI-VÉRITÉ : un
          terminal DÉCLARÉ par la Console apparaît ici même s’il n’a jamais synchronisé (la requête
          joint les équipements déclarés). Laisser croire qu’il faut d’abord l’allumer envoie chercher
          une panne d’appareil là où c’est la déclaration qui manque. */}
      {terminals === null ? (
        <EmptyState icon="tablet" title={t('Terminaux non lisibles')}
          note={t('La lecture des terminaux a échoué pour ce magasin — le détail est dans les avis de l’écran.')} />
      ) : terminals.length === 0 ? (
        <EmptyState icon="tablet" title={t('Aucun terminal de stock')}
          note={t('Un terminal Sliderz Mobile Partner apparaît ici dès que la Console l’a déclaré — même avant sa première synchronisation. Son code (MP01) vient de la Console Orgazone.')} />
      ) : terminals.map((terminal) => <LigneTerminal key={terminal.deviceId} terminal={terminal} />)}

      <div style={{ padding: '12px 16px', background: 'var(--ink-50)', fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.6 }}>
        {phraseProvenance(t('un terminal déjà affecté'))}
      </div>
    </div>
  );
}

/** Un terminal : son code (ou son identifiant), sa famille, son état de synchronisation — le même que celui d'une caisse. */
function LigneTerminal({ terminal }: { terminal: StoreTerminalRow }) {
  const vue = etatCaisse(terminal.lastSyncAt);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 16px', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
      <span style={{ width: 30, height: 30, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name="barcode" size={18} stroke={1.8} />
      </span>
      <span style={{ flex: 1, minWidth: 120 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>
            {terminal.equipmentCode ?? t('terminal {v}', { v: terminal.deviceId.slice(0, 8) })}
          </span>
          <span aria-hidden style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: vue.point }} />
        </span>
        <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 1 }}>
          {vue.sous}{terminal.equipmentCode === null ? ' · ' + t('sans fiche à la Console') : ''}
        </span>
      </span>
      <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{terminal.family ?? t('terminal de stock')}</span>
    </div>
  );
}

/**
 * L'état d'une caisse, DÉDUIT de son dernier battement — jamais d'un statut stocké.
 * ⚠️ Trois situations, trois phrases : jamais vue (mise en service à finir), vue récemment (en
 * service), vue il y a longtemps (à aller rallumer). Les confondre enverrait chercher la panne au
 * mauvais endroit.
 */
export function etatCaisse(lastSyncAt: number | null, now = Date.now()): { point: string; sous: string; enService: boolean } {
  if (lastSyncAt === null) return { point: 'var(--ink-300)', sous: t('jamais vue en synchronisation'), enService: false };
  const minutes = Math.max(0, Math.round((now - lastSyncAt) / 60_000));
  if (minutes <= EN_LIGNE_APRES_MIN) return { point: 'var(--teal-500)', sous: t('en service'), enService: true };
  if (minutes < 60) return { point: 'var(--amber)', sous: t('vue il y a {v} min', { v: minutes }), enService: false };
  const heures = Math.round(minutes / 60);
  if (heures < 48) return { point: 'var(--amber)', sous: t('vue il y a {v} h', { v: heures }), enService: false };
  return { point: 'var(--rose)', sous: t('vue il y a {v} j', { v: Math.round(heures / 24) }), enService: false };
}

/** Les groupes d'affichage — un raccourci du back-office, jamais une configuration de caisse. */
function CarteGroupes({ vue, nouveau, setNouveau, busy, onCreer, onBasculer, onSupprimer }: {
  vue: StoreTopology; nouveau: string; setNouveau: (v: string) => void; busy: boolean;
  onCreer: () => void;
  onBasculer: (g: RegisterGroup, registerId: string) => void;
  onSupprimer: (g: RegisterGroup) => void;
}) {
  const caisses = useMemo(() => (vue.registers ?? []).map((r) => r.registerId), [vue.registers]);
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
      <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--ink-100)' }}>
        <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Groupes de caisses')}</div>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.5 }}>
          <Trans k='Des raccourcis d’affichage pour le back-office — « les caisses du 2ᵉ étage » en un clic dans le journal. <1>Jamais descendus aux caisses.</1>' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
        </div>
      </div>

      {vue.groups === null ? (
        <EmptyState icon="alert" title={t('Groupes non lisibles')}
          note={t('La lecture des groupes a échoué — mieux vaut ne pas en créer tant qu’on ne sait pas ce qui existe déjà.')} />
      ) : vue.groups.length === 0 ? (
        <EmptyState icon="layers" title={t('Aucun groupe')}
          note={t('Créez-en un ci-dessous, puis cliquez les caisses à y ranger.')} />
      ) : vue.groups.map((g) => (
        <div key={g.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--ink-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
            <span style={{ flex: 1, fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{g.name}</span>
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
              {t('{n} caisse', { n: g.registerIds.length })}
            </span>
            <Tooltip content={t('Supprimer le groupe')}>
              <button type="button" onClick={() => onSupprimer(g)} disabled={busy} aria-label={t('Supprimer le groupe {nom}', { nom: g.name })}
                style={{ width: 26, height: 26, border: 'none', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: busy ? 'not-allowed' : 'pointer' }}>
                <Icon name="trash" size={12} stroke={2} />
              </button>
            </Tooltip>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {/* ⚠️ TOUTES les caisses du magasin sont offertes, celles DEDANS étant simplement teintées :
                une liste réduite aux membres ne permettrait pas d'en ajouter. */}
            {caisses.length === 0
              ? <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('Aucune caisse déclarée à ranger.')}</span>
              : caisses.map((id) => {
                const dedans = g.registerIds.includes(id);
                return (
                  <button key={id} type="button" onClick={() => onBasculer(g, id)} disabled={busy}
                    aria-pressed={dedans}
                    style={{
                      height: 28, padding: '0 11px', borderRadius: 'var(--r-pill)',
                      border: `1.5px solid ${dedans ? 'var(--teal-500)' : 'var(--ink-200)'}`,
                      background: dedans ? 'var(--teal-100)' : 'var(--paper)',
                      color: dedans ? 'var(--teal-800)' : 'var(--ink-500)',
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', fontWeight: 700,
                      cursor: busy ? 'not-allowed' : 'pointer',
                    }}>{codeCaisse(id)}</button>
                );
              })}
          </div>
          {/* ⚠️ Un groupe VIDE se dit : sans cette phrase, il se lit comme un chargement raté. */}
          {g.registerIds.length === 0 && caisses.length > 0 && (
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 6 }}>

              {t('Groupe vide — cliquez une caisse pour l’y ranger.')}
            </div>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, padding: '12px 16px', background: 'var(--ink-50)' }}>
        <input
          value={nouveau} onChange={(e) => setNouveau(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onCreer(); }}
          placeholder={t('Nom du groupe — ex. 2ᵉ étage')} maxLength={60}
          style={{ flex: 1, minWidth: 0, height: 36, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-body)', color: 'var(--ink-900)', boxSizing: 'border-box', outline: 'none' }} />
        <button type="button" onClick={onCreer} disabled={busy || !nouveau.trim()}
          style={{ height: 36, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)', color: '#fff', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', cursor: busy || !nouveau.trim() ? 'not-allowed' : 'pointer', opacity: busy || !nouveau.trim() ? 0.5 : 1 }}>

          {t('Créer le groupe')}
        </button>
      </div>
    </div>
  );
}
